import { useState, useRef, useCallback, useEffect } from 'react'
import { Container, Form, Button, Tabs, Tab, Card, Badge, Spinner, Alert, ProgressBar } from 'react-bootstrap'
import { useProfile } from '../contexts/ProfileContext'
import { useToast } from '../contexts/ToastContext'
import searchService from '../services/searchService'
import SitemapCollapse from '../components/SitemapCollapse'
import ReportModal from '../components/ReportModal'

const PAGE_SIZE = 25

function Search() {
  const { activeProfile } = useProfile()
  const { showInfo } = useToast()
  const [keywords, setKeywords] = useState('')
  const [selectedTypes, setSelectedTypes] = useState(['website'])
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState(null)
  const [metadata, setMetadata] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [reportItem, setReportItem] = useState(null)
  // Track page boundaries: { type: [startIndex1, startIndex2, ...] }
  const [pageBoundaries, setPageBoundaries] = useState({})
  // Track which page we're about to load for scrolling
  const [pendingScrollPage, setPendingScrollPage] = useState(null)

  // Ref to store the abort function for the current stream
  const abortStreamRef = useRef(null)
  // Ref for scrolling to new results
  const newPageRef = useRef(null)

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (abortStreamRef.current) {
        abortStreamRef.current()
      }
    }
  }, [])

  // Scroll to new results when loading completes
  useEffect(() => {
    if (!streaming && pendingScrollPage && newPageRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        newPageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setPendingScrollPage(null)
      }, 100)
    }
  }, [streaming, pendingScrollPage])

  const handleSearch = useCallback((e, page = 1) => {
    if (e) e.preventDefault()

    if (!activeProfile) {
      setError('Please select a profile first')
      return
    }

    if (!keywords.trim()) {
      showInfo('Please enter search keywords')
      return
    }

    // Abort any existing stream
    if (abortStreamRef.current) {
      abortStreamRef.current()
    }

    // Reset state for new search (page 1) or keep existing for pagination
    if (page === 1) {
      setResults(null)
      setMetadata(null)
      setPageBoundaries({})
      setPendingScrollPage(null)
    } else {
      // Track which page we're loading for scroll behavior
      setPendingScrollPage(page)
    }

    setLoading(true)
    setStreaming(true)
    setError(null)
    setCurrentPage(page)

    const searchData = {
      profile_id: activeProfile.id,
      keywords: keywords.trim(),
      scorable_types: selectedTypes.length > 0 ? selectedTypes : undefined,
      age_restrict: activeProfile.age_restrict_results,
      page: page
    }

    // Start streaming search
    abortStreamRef.current = searchService.searchStream(searchData, {
      onMetadata: (data) => {
        setMetadata(data)
        setLoading(false)
      },
      onResults: (data) => {
        const { type, items } = data

        setResults(prev => {
          const newResults = prev ? { ...prev } : {}

          if (page === 1) {
            // First page - replace results for this type
            newResults[type] = items
          } else {
            // Subsequent pages - append results
            // Track page boundary before appending
            const currentLength = newResults[type]?.length || 0
            setPageBoundaries(prevBoundaries => ({
              ...prevBoundaries,
              [type]: [...(prevBoundaries[type] || []), currentLength]
            }))
            newResults[type] = [...(newResults[type] || []), ...items]
          }

          return newResults
        })
      },
      onComplete: (data) => {
        setStreaming(false)
        setMetadata(prev => ({
          ...prev,
          ...data
        }))
      },
      onError: (err) => {
        setError(err.message || 'Search failed')
        setLoading(false)
        setStreaming(false)
      }
    })
  }, [activeProfile, keywords, selectedTypes])

  const handleLoadMore = useCallback(() => {
    if (metadata?.has_more && !loading && !streaming) {
      handleSearch(null, currentPage + 1)
    }
  }, [metadata, loading, streaming, currentPage, handleSearch])

  const ResultCard = ({ item }) => {
    // Extract site name from URL
    let siteName = ''
    try {
      const url = new URL(item.thing)
      siteName = url.hostname.replace(/^www\./, '')
    } catch {
      siteName = item.thing
    }

    return (
      <Card className="mb-3 scorable-card">
        <Card.Body>
          <div className="d-flex align-items-start gap-3">
            {/* Fixed-width thumbnail column */}
            <div style={{ width: '200px', flexShrink: 0 }}>
              {item.thumbnail_path && (
                <img
                  src={item.thumbnail_path}
                  alt="Thumbnail"
                  style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                  className="rounded"
                />
              )}
            </div>
            {/* Content column */}
            <div className="flex-grow-1 min-width-0">
              {/* Favicon + Site info row */}
              <div className="d-flex align-items-start gap-2 mb-1">
                {/* Favicon */}
                {item.favicon_path && (
                  <img
                    src={item.favicon_path}
                    alt=""
                    className="result-favicon"
                    style={{ width: '32px', height: '32px', flexShrink: 0 }}
                  />
                )}
                {/* Site name and URL column */}
                <div className="min-width-0">
                  {/* Site name - medium type, plain styled */}
                  <div className="result-site-name text-muted">
                    <a href={item.thing} target="_blank" rel="noopener noreferrer">
                      {siteName}
                    </a>
                  </div>
                  {/* Full URL - smaller type, plain styled */}
                  <div className="result-url text-muted text-break">
                    <a href={item.thing} target="_blank" rel="noopener noreferrer">
                      {item.thing}
                    </a>
                  </div>
                </div>
              </div>
              {/* Page title - larger type, typical hyperlink styling */}
              <div className="result-page-title d-flex flex-wrap align-items-center gap-1">
                <a href={item.thing} target="_blank" rel="noopener noreferrer">
                  {item.pretty_name || item.thing}
                </a>
                {item.has_individual_source ? (
                  <Badge
                    bg=""
                    className="suggestion-badge personal"
                    title="Recommended by your personally trusted connections"
                  >
                    Personal suggestion
                  </Badge>
                ) : (
                  <Badge
                    bg=""
                    className="suggestion-badge network"
                    title="Recommended by your trusted networks"
                  >
                    Network suggestion
                  </Badge>
                )}
              </div>
              {/* Summary */}
              {item.summary && <div className="result-summary">{item.summary}</div>}
              <div className="mt-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <small className="text-muted">
                  Trusted by {item.source_count} source{item.source_count !== 1 ? 's' : ''}
                </small>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setReportItem(item)}
                >
                  Report
                </Button>
              </div>
              <SitemapCollapse
                hasSitemap={item.has_sitemap}
                sitemapEntries={item.sitemap_entries}
              />
            </div>
          </div>
        </Card.Body>
      </Card>
    )
  }

  const renderResults = (type, label) => {
    if (!results || !results[type] || results[type].length === 0) {
      return <p className="text-muted">No {label} found</p>
    }

    const boundaries = pageBoundaries[type] || []
    // Get the most recent boundary for the pending scroll page
    const latestBoundary = boundaries.length > 0 ? boundaries[boundaries.length - 1] : null

    return results[type].map((item, index) => {
      const isPageBoundary = boundaries.includes(index)
      // Scroll target is the LAST item before the new page, so user sees:
      // result 25 -> <hr> -> result 26+
      const isScrollTarget = latestBoundary !== null && index === latestBoundary - 1 && pendingScrollPage

      return (
        <div
          key={item.id}
          ref={isScrollTarget ? newPageRef : null}
          style={isScrollTarget ? { scrollMarginTop: '20px' } : undefined}
        >
          {isPageBoundary && (
            <hr className="my-4 page-divider" />
          )}
          <ResultCard item={item} />
        </div>
      )
    })
  }

  const renderTabTitle = (label, type) => {
    const count = results?.[type]?.length || 0
    if (count === 0) {
      return label
    }
    const displayCount = count > 50 ? '50+' : count
    return (
      <>
        {label} <Badge bg="secondary" pill>{displayCount}</Badge>
      </>
    )
  }

  const showEndOfResults = metadata && !metadata.has_more && metadata.total_results > 0

  return (
    <Container className="mt-4">
      <h2>Search</h2>

      <Form onSubmit={handleSearch} className="mb-4">
        <div className="row mb-3">
          <div className="col-md-8">
            <Form.Control
              type="text"
              placeholder="Enter search keywords..."
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              size="lg"
            />
          </div>
          <div className="col-md-4 mt-2 mt-md-0">
            <Button type="submit" variant="primary" size="lg" disabled={loading}>
              {loading ? <><Spinner size="sm" className="me-2" /> Searching...</> : 'Search'}
            </Button>
          </div>
        </div>

        <div className="mb-3">
          <div className="d-flex flex-wrap gap-2">
            {[
              { value: 'website', label: 'Websites' },
              { value: 'app', label: 'Apps' },
              { value: 'person', label: 'People' },
              { value: 'service', label: 'Services' },
              { value: 'recipe', label: 'Recipes' },
              { value: 'geoplace', label: 'Places' },
              { value: 'physicalobject', label: 'Objects' }
            ].map(type => (
              <Button
                key={type.value}
                variant={selectedTypes.includes(type.value) ? 'primary' : 'outline-primary'}
                size="sm"
                className="type-toggle-btn"
                onClick={() => {
                  setSelectedTypes(prev =>
                    prev.includes(type.value)
                      ? prev.filter(t => t !== type.value)
                      : [...prev, type.value]
                  )
                }}
              >
                {type.label}
              </Button>
            ))}
            <span className="text-muted align-self-center">(Choose)</span>
          </div>
        </div>

      </Form>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Streaming/Loading indicator */}
      {streaming && metadata && (
        <Alert variant="info" className="d-flex align-items-center">
          <Spinner size="sm" className="me-2" />
          Loading results... ({metadata.total_results} total found)
        </Alert>
      )}

      {/* Results metadata */}
      {metadata && !loading && (
        <div className="mb-3 text-muted">
          <small>
            Found {metadata.total_results} results
            {metadata.total_results > 0 && (
              <> &middot; Showing page {metadata.page}</>
            )}
          </small>
        </div>
      )}

      {/* Pre-search background image - shown before any search is initiated */}
      {results === null && !loading && (
        <div className="search-presearch-image text-center py-4">
          <img
            src="/bagel-search.jpg"
            alt="Search for trusted recommendations"
            className="rounded"
            style={{ maxWidth: '100%', maxHeight: '800px', objectFit: 'contain' }}
          />
        </div>
      )}

      {results && !loading && (
        <div className="search-results-tabs">
          <Tabs defaultActiveKey="website" id="results-tabs" className="mb-3">
            <Tab eventKey="website" title={renderTabTitle('Websites', 'website')}>
              {renderResults('website', 'websites')}
            </Tab>
            <Tab eventKey="app" title={renderTabTitle('Apps', 'app')}>
              {renderResults('app', 'apps')}
            </Tab>
            <Tab eventKey="person" title={renderTabTitle('People', 'person')}>
              {renderResults('person', 'people')}
            </Tab>
            <Tab eventKey="service" title={renderTabTitle('Services', 'service')}>
              {renderResults('service', 'services')}
            </Tab>
            <Tab eventKey="recipe" title={renderTabTitle('Recipes', 'recipe')}>
              {renderResults('recipe', 'recipes')}
            </Tab>
            <Tab eventKey="geoplace" title={renderTabTitle('Places', 'geoplace')}>
              {renderResults('geoplace', 'places')}
            </Tab>
            <Tab eventKey="physicalobject" title={renderTabTitle('Objects', 'physicalobject')}>
              {renderResults('physicalobject', 'objects')}
            </Tab>
          </Tabs>

          {/* Load More button */}
          {metadata?.has_more && (
            <div className="text-center mt-4 mb-4">
              <Button
                variant="outline-primary"
                onClick={handleLoadMore}
                disabled={streaming}
              >
                {streaming ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Loading...
                  </>
                ) : (
                  `Load More Results (Page ${currentPage + 1})`
                )}
              </Button>
            </div>
          )}

        </div>
      )}

      {/* Report Modal */}
      <ReportModal
        show={reportItem !== null}
        onHide={() => setReportItem(null)}
        item={reportItem}
      />
    </Container>
  )
}

export default Search
